/**
 * S-62 漁場情報 詳細（F-CAT-01）。担当: G4
 */
import type { ReactNode } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Pencil } from 'lucide-react';
import { Button, DataTable, ErrorState, Loading, PageHeader, Panel, type Column } from '@/components';
import { hasRole, READ_ONLY_ROLES } from '@/auth/types';
import { useCurrentUser } from '@/auth/AuthProvider';
import { useFiscalYear } from '@/fiscal-year/FiscalYearProvider';
import { formatNumber, formatYen } from '@/lib/format';
import { toWareki } from '@/lib/wareki';
import { useFishingGround, type FishingGroundDetail } from './api';
import './catch.css';

type Entry = FishingGroundDetail['entries'][number];

const dms = (d: number | null, m: number | null, s: number | null) => (d == null && m == null ? '' : `${d ?? ''}°${m ?? ''}′${s ?? ''}″`);

export function FishingGroundDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const user = useCurrentUser();
  const { yearPath } = useFiscalYear();
  const { data, isPending, error, refetch } = useFishingGround(Number(id));
  if (isPending) return <Loading block />;
  if (error) return <ErrorState error={error} onRetry={() => void refetch()} />;
  const canEdit = !hasRole(user, READ_ONLY_ROLES);

  const columns: Column<Entry>[] = [
    { key: 'no', header: 'No.', width: 48, render: (_r, i) => <span className="bs-num">{i + 1}</span> },
    { key: 'surveyDate', header: '漁場調査日', width: 104, render: (r) => <span className="bs-num">{r.surveyDate}</span> },
    { key: 'landingPort', header: '水揚港', width: 90 },
    { key: 'zoneNo', header: '農林漁区（手入力）', numeric: true, width: 90 },
    { key: 'zoneNoCalculated', header: '農林漁区（機械算出）', numeric: true, width: 96 },
    { key: 'lat', header: '緯度', width: 110, render: (r) => <span className="bs-num">{dms(r.latDeg, r.latMin, r.latSec)}</span> },
    { key: 'lon', header: '経度', width: 120, render: (r) => <span className="bs-num">{dms(r.lonDeg, r.lonMin, r.lonSec)}</span> },
    { key: 'weather', header: '天候', width: 64 },
    { key: 'wind', header: '風向・風力', width: 90, render: (r) => [r.windDirection, r.windForce].filter(Boolean).join('・') },
    { key: 'waterTemp', header: '表面水温(℃)', numeric: true, width: 96 },
    { key: 'depth', header: '水深', numeric: true, width: 70 },
    { key: 'standardName', header: '標準和名', width: 110 },
    { key: 'localName', header: '地方名', width: 100 },
    { key: 'count', header: '尾数', numeric: true, width: 64 },
    { key: 'weight', header: '重量(kg)', numeric: true, width: 84, render: (r) => formatNumber(r.weight, 2) },
    { key: 'note', header: '備考', width: 140 },
  ];

  const item = (label: string, value: ReactNode) => (
    <div className="bs-catch-dl__row">
      <dt>{label}</dt>
      <dd>{value || '—'}</dd>
    </div>
  );

  return (
    <>
      <PageHeader
        eyebrow="漁獲調査"
        title={`漁場情報 ${data.vesselName}`}
        meta={
          <span className="bs-num">
            {data.voyageFrom} 〜 {data.voyageTo}
          </span>
        }
        actions={
          canEdit && (
            <Button variant="primary" icon={<Pencil size={16} />} onClick={() => navigate(yearPath(`fishing-grounds/${data.id}/edit`))}>
              編集
            </Button>
          )
        }
      />
      <Panel title="基本情報">
        <dl className="bs-catch-dl">
          {item('事業実施者', data.businessOperatorName)}
          {item('漁船登録番号', <span className="bs-num">{data.registrationNo}</span>)}
          {item('船名', data.vesselName)}
          {item('船主名', data.ownerName)}
          {item('漁法', data.fishingMethod)}
          {item('漁具・数量', [data.gear, data.gearCount != null ? `${data.gearCount}` : null].filter(Boolean).join(' ／ '))}
          {item('調査航海期間', `${toWareki(data.voyageFrom)} 〜 ${toWareki(data.voyageTo)}`)}
          {item('報告者氏名', data.reporterName)}
          {item('水揚げ日', data.landingDate ? toWareki(data.landingDate) : '')}
          {item('調査・操業漁場', data.fishingGround)}
          {item('水揚合計額（税込）', data.landingTotalInclTax != null ? formatYen(data.landingTotalInclTax) : '')}
          {item('出荷控除額（税込）', data.shippingDeductionInclTax != null ? formatYen(data.shippingDeductionInclTax) : '')}
          {item('手取金額（税抜）', data.netAmountExclTax != null ? formatYen(data.netAmountExclTax) : '')}
        </dl>
      </Panel>
      <h2 className="bs-catch-h2">漁場情報（明細）</h2>
      <DataTable caption="漁場情報（明細）" columns={columns} rows={data.entries} rowKey={(r) => r.id} dense />
    </>
  );
}
